import type { Card } from '../trick/cards.js';
import { fullDeck, sameCard } from '../trick/cards.js';
import { clockwiseAfter } from '../bridge/seats.js';
import { trickWinner } from '../trick/rules.js';
import type { PirateView } from './view.js';

/** `easy` guesses loosely and plays any legal card. `clever` guesses from its hand and then plays to hit its guess exactly. */
export type PirateRobotLevel = 'easy' | 'clever';

const lowest = (cards: readonly Card[]): Card => cards.reduce((a, b) => (b.rank < a.rank ? b : a));
const highest = (cards: readonly Card[]): Card => cards.reduce((a, b) => (b.rank > a.rank ? b : a));

const TRUMP_STRENGTH: Readonly<Record<number, number>> = { 14: 1, 13: 0.85, 12: 0.65, 11: 0.45, 10: 0.3 };
const SIDE_STRENGTH: Readonly<Record<number, number>> = { 14: 0.8, 13: 0.4, 12: 0.15 };

/**
 * Self-play (400 deals per size, `scripts/pirate-calibrate.ts`) showed the raw card values undercount, because long
 * suits and low trumps also win tricks. 1.3 gave the best exact-guess rate: 46% on 13 cards (1.0: 34%, 1.5: 43%,
 * 1.9: 28%); the easy robot hits about 18%.
 */
export const ESTIMATE_SCALE = 1.3;

/**
 * Big Cards (8 to Ace): every card in a 7-card hand is a high card, so the full-deck scale over-bids (hit rate 31% on 7 cards, bias
 * +0.7). Swept 0.5..1.3 over 800 deals per size (`scripts/big-cards-strength.ts`): 0.9 gives about 50% on 7 cards and no bias.
 */
export const BIG_CARDS_ESTIMATE_SCALE = 0.9;

/** A rough count of the tricks a hand will win: top trumps and side-suit aces. */
export function estimateTricks(view: PirateView): number {
  let total = 0;
  for (const c of view.hand) {
    total += c.suit === view.trump ? (TRUMP_STRENGTH[c.rank] ?? 0.12) : (SIDE_STRENGTH[c.rank] ?? 0);
  }
  return total * (view.minRank >= 8 ? BIG_CARDS_ESTIMATE_SCALE : ESTIMATE_SCALE);
}

/**
 * Cap'n's Hat: the chance that the viewer's unseen card wins the one trick. Exact, not estimated: the
 * card is any card that is neither another seat's visible card nor the trump card, all equally likely,
 * and with one card each there are no choices, so the winner follows from the rules alone.
 */
export function hatWinChance(view: PirateView): number {
  const seen = [view.trumpCard, ...Object.values(view.hatCards ?? {})];
  const unseen = fullDeck(view.minRank).filter((c) => !seen.some((k) => sameCard(k, c)));
  if (unseen.length === 0) return 0;
  const order = clockwiseAfter(view.dealer);
  let wins = 0;
  for (const mine of unseen) {
    const plays = order.map((seat) => ({ seat, card: seat === view.viewer ? mine : (view.hatCards?.[seat] as Card) }));
    if (trickWinner(plays, view.trump) === view.viewer) wins += 1;
  }
  return wins / unseen.length;
}

/** The robot's secret guess, 0..cardsPerHand. */
export function pirateChooseGuess(view: PirateView, level: PirateRobotLevel, random: () => number): number {
  if (view.hat) {
    // Bet 1 when the card is more likely than not to win. Easy shades the chance by up to 0.3 either way.
    const chance = hatWinChance(view) + (level === 'easy' ? random() * 0.6 - 0.3 : 0);
    return chance > 0.5 ? 1 : 0;
  }
  const estimate = estimateTricks(view);
  const noisy = level === 'easy' ? estimate + (random() * 4 - 2) : estimate;
  return Math.max(0, Math.min(view.cardsPerHand, Math.round(noisy)));
}

/**
 * The card a robot plays. The clever robot knows its own guess: while it still wants tricks it wins
 * them as cheaply as it can, and once it has enough it sheds high cards and avoids winning.
 */
export function pirateChooseCard(view: PirateView, level: PirateRobotLevel, random: () => number): Card {
  const legal = view.legalPlays;
  if (legal.length === 0) throw new Error("pirateChooseCard: nothing to play (not this seat's turn?)");
  if (legal.length === 1) return legal[0] as Card;
  if (level === 'easy') return legal[Math.floor(random() * legal.length)] as Card;

  const guess = view.guesses?.[view.viewer] ?? view.ownGuess ?? 0;
  const wantMore = view.tricksWon[view.viewer] < guess;
  const wins = (c: Card) => trickWinner([...view.trick, { seat: view.viewer, card: c }], view.trump) === view.viewer;
  let card: Card;
  if (view.trick.length === 0) {
    card = wantMore ? highest(legal) : lowest(legal);
  } else if (wantMore) {
    const winning = legal.filter(wins);
    card = winning.length > 0 ? lowest(winning) : lowest(legal);
  } else {
    const losing = legal.filter((c) => !wins(c));
    card = losing.length > 0 ? highest(losing) : lowest(legal);
  }
  return legal.find((c) => sameCard(c, card)) ?? (legal[0] as Card);
}
